import { useEffect, useRef, useState } from 'react'
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
  type QueryMeta,
} from '@tanstack/react-query'
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router'

import { useAuth } from '@/lib/auth'
import { useDebouncedValue } from '@/lib/use-debounced-value'
import { useLocalStorageState } from '@/lib/use-local-storage-state'
import { downloadBlob } from '@/lib/download-blob'
import type { FieldFilter } from '@/lib/dashboards'

import {
  createDataGridClient,
  type DataGridExport,
  type DataGridExportRequest,
  type DataGridRequest,
} from '../api/data-grid-client'
import type { DataGridSavedView, DataGridSavedViewState } from '../api/data-grid-saved-views-api'
import { recordedOrigin, withRecordedOrigin } from './history-origin'
import { appliedDataGridSearch, type DataGridTable } from './table-state'
import {
  decodeDataGridColumns,
  decodeDataGridParameters,
  decodeDataGridUrlState,
  encodeDataGridColumns,
  encodeDataGridParameters,
  encodeDataGridUrlState,
  normalizeDataGridUrlState,
} from './url-state'
import {
  useDataGridSavedViews,
  type DataGridSavedViewsOptions,
} from './use-data-grid-saved-views'
import type {
  AnyDataGridContract,
  ColumnId,
  DataGridColumnSchema,
  DataGridCriteria,
  DataGridContractFilter,
  DataGridExportFormat,
  DataGridFacet,
  DataGridFilter,
  DataGridQuery,
  DataGridResponse,
  DataGridSort,
  DataGridParameterValue,
  ParameterId,
} from './types'

type UseDataGridCommonOptions<Contract extends AnyDataGridContract> = {
  contract: Contract
  queryKey: readonly unknown[]
  meta?: QueryMeta
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean)
  pageSize?: number
  paginationMode?: 'incremental' | 'pages'
  searchDebounceMs?: number
  savedViews?: DataGridSavedViewsOptions
  enabled?: boolean
  hiddenColumns?: readonly ColumnId<Contract>[]
  urlState?: boolean
  refetchInterval?: number | false | ((items: DataGridResponse<Contract>['items']) => number | false)
  columnStorageKey?: string
  /** The columns this grid shows before the user chooses, in place of the contract's defaults. */
  defaultColumns?: readonly ColumnId<Contract>[]
  /**
   * Filters from an enclosing dashboard. Each applies to the column whose `field` matches; filters on fields this grid
   * does not carry are ignored. They are sent with every request but stay out of the grid's own filters and URL state.
   * While one applies, its column is not filterable in the grid, and any filter the grid kept on that column is set aside.
   */
  baseFilters?: readonly FieldFilter[]
}

type DataGridEndpointOptions<Contract extends AnyDataGridContract> = {
  endpoint: string
  queryFn?: (input: DataGridRequest<Contract>) => Promise<DataGridResponse<Contract>>
  exportFn?: (input: DataGridExportRequest<Contract>) => Promise<DataGridExport>
}

type DataGridCustomRequestOptions<Contract extends AnyDataGridContract> = {
  endpoint?: never
  queryFn: (input: DataGridRequest<Contract>) => Promise<DataGridResponse<Contract>>
  exportFn?: (input: DataGridExportRequest<Contract>) => Promise<DataGridExport>
}

export type UseDataGridOptions<Contract extends AnyDataGridContract> =
  UseDataGridCommonOptions<Contract>
  & (DataGridEndpointOptions<Contract> | DataGridCustomRequestOptions<Contract>)

type StoredColumns<Column extends string> = {
  selected: Column[]
  order: Column[]
}

export const DEFAULT_SEARCH_DEBOUNCE_MS = 250

const DATA_GRID_SEARCH_KEYS = ['q', 'qColumns', 'filters', 'sort', 'columns']

export function useDataGrid<Contract extends AnyDataGridContract>(
  options: UseDataGridOptions<Contract>,
) {
  const {
    contract,
    queryKey,
    pageSize = 50,
    paginationMode = 'incremental',
    searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
    savedViews,
    enabled = true,
    urlState = true,
  } = options
  const { queryFn, exportFn } = resolveRequests(options)
  type Column = ColumnId<Contract>
  type Parameter = ParameterId<Contract>
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const exportAbortRef = useRef<AbortController | null>(null)
  const facetAbortRef = useRef<Record<string, AbortController>>({})
  const [exportingFormat, setExportingFormat] = useState<DataGridExportFormat | null>(null)
  const [exportError, setExportError] = useState<Error | null>(null)
  const [facetState, setFacetState] = useState<Partial<Record<Column, {
    criteriaKey: string
    search: string
    isLoading: boolean
    error: Error | null
    facet?: DataGridFacet
  }>>>({})
  const routeSearch = useSearch({ strict: false }) as Record<string, unknown>
  const routeOriginId = useLocation({ select: (location) => recordedOrigin(location.state, contract.id) })
  const [memory, setMemory] = useState<{ search: Record<string, unknown>; originId: string | null }>({
    search: {},
    originId: null,
  })
  const gridSearch = urlState ? routeSearch : memory.search
  const restoredOriginId = urlState ? routeOriginId : memory.originId
  const originRef = useRef(restoredOriginId)
  const gridSearchKeys = [...DATA_GRID_SEARCH_KEYS, ...Object.keys(contract.parameters ?? {})]
  const historyIndex = useLocation({ select: (location) => location.state.__TSR_index })
  const bareLocation = useLocation({
    select: (location) => gridSearchKeys.every((key) => (location.search as Record<string, unknown>)[key] === undefined),
  })
  const bare = urlState ? bareLocation : gridSearchKeys.every((key) => memory.search[key] === undefined)
  const validColumns = new Set(Object.keys(contract.columns))
  const criteria = decodeDataGridUrlState<Column>(gridSearch, contract.columns)
  const parameters = decodeDataGridParameters(gridSearch, contract.parameters) as Record<Parameter, DataGridParameterValue>
  const searchInput = criteria.search?.query ?? ''
  const debouncedSearch = useDebouncedValue(searchInput, searchDebounceMs).trim()
  const base = gridFiltersFromFields(contract, options.baseFilters) as DataGridContractFilter<Contract>[]
  const baseColumns = new Set(base.map((entry) => entry.column))
  const ownFilters = criteria.filter?.filter((entry) => !baseColumns.has(entry.column)) ?? []
  // The dashboard owns a column's filter while it filters that column, so the grid's controls leave it alone.
  const lockFilter = (column: DataGridColumnSchema) =>
    baseColumns.has(column.id) && column.filterable ? { ...column, filterable: false } : column
  const activeCriteria: DataGridCriteria<Column> = {
    ...criteria,
    filter: base.length + ownFilters.length ? [...base, ...ownFilters] : undefined,
    sort: criteria.sort ?? contract.defaultSort,
    search: debouncedSearch.length >= contract.searchMinimumLength
      ? { ...criteria.search, query: debouncedSearch }
      : undefined,
  }
  const userId = useAuth().user?.userId
  const sourceQueryKey = [...queryKey, {
    dataGridUser: userId,
    dataGridContract: contract.id,
    dataGridParameters: Object.keys(parameters).length === 0 ? undefined : parameters,
  }]
  const sourceKey = JSON.stringify(sourceQueryKey)
  useEffect(() => {
    for (const controller of Object.values(facetAbortRef.current)) controller.abort()
    facetAbortRef.current = {}
    setFacetState({})
    return () => {
      for (const controller of Object.values(facetAbortRef.current)) controller.abort()
      facetAbortRef.current = {}
    }
  }, [sourceKey])
  useEffect(() => () => exportAbortRef.current?.abort(), [])
  const defaultColumns = options.defaultColumns
    ? [...options.defaultColumns]
    : Object.values(contract.columns)
      .filter((column) => !column.hiddenByDefault)
      .map((column) => column.id as Column)
  const [storedColumns, setStoredColumns] = useLocalStorageState<StoredColumns<Column>>(
    userId === undefined ? null : `data-grid:${userId}:${options.columnStorageKey ?? contract.id}:columns`,
    (raw) => parseStoredColumns<Column>(raw, validColumns),
    JSON.stringify,
    { selected: defaultColumns, order: Object.keys(contract.columns) as Column[] },
  )
  const hiddenColumns = new Set<string>(options.hiddenColumns ?? [])
  const showsAnyColumn = (columns: readonly string[]) =>
    columns.some((column) => column in contract.columns && !hiddenColumns.has(column))
  const shown = (column: string) => !hiddenColumns.has(column)
  const urlColumns = decodeDataGridColumns<Column>(gridSearch.columns, contract.columns)
  const storedOrder = normalizeOrder(contract, storedColumns.order)
  const storedSelectedColumns = showsAnyColumn(storedColumns.selected)
    ? normalizeSelectedColumns(contract, storedColumns, defaultColumns)
    : defaultColumns
  const storedOrderedColumns = storedOrder
    .filter((column) => storedSelectedColumns.includes(column) && shown(column))
  const selectedColumns = urlColumns && showsAnyColumn(urlColumns) ? urlColumns : storedSelectedColumns
  const columnOrder = urlColumns ? fillVisibleSlots(storedOrder, urlColumns) : storedOrder
  const orderedColumns = columnOrder.filter((column) => selectedColumns.includes(column) && shown(column))
  const searchDebouncing = searchInput.trim() !== debouncedSearch
  const defaultTable = tableFromSearch({ columns: encodeDataGridColumns(defaultColumns) })
  const savedViewsState = useDataGridSavedViews({
    gridId: contract.id,
    userId,
    table: tableFromSearch(gridSearch),
    defaultTable,
    everythingTable: urlColumns ? defaultTable : { ...defaultTable, columns: storedOrderedColumns },
    viewTable: (filters, state) => {
      const table = tableFromSearch(savedViewSearch(filters, state))
      return table.filters.length === filters.length ? table : null
    },
    settled: !searchDebouncing,
    apply: applySavedView,
    restoredOriginId,
    originRef,
    entry: { key: JSON.stringify([urlState ? historyIndex : null, userId, ...queryKey]), bare },
    options: savedViews,
  })
  const awaitingDefaultView = savedViewsState?.awaitingDefault ?? false
  const queryEnabled = enabled && userId !== undefined && !awaitingDefaultView
  const queryColumns = (Object.keys(contract.columns) as Column[]).filter((column) => orderedColumns.includes(column))
  const cursorCriteriaKey = JSON.stringify([paginationMode, sourceQueryKey, activeCriteria, queryColumns])
  const [cursorPages, setCursorPages] = useState({
    criteriaKey: '', pages: [{ after: '', offset: 0 }],
  })
  const pageHistory = cursorPages.criteriaKey === cursorCriteriaKey
    ? cursorPages.pages : [{ after: '', offset: 0 }]
  const currentPage = pageHistory.at(-1) ?? { after: '', offset: 0 }
  useEffect(() => {
    if (paginationMode !== 'pages') return
    setCursorPages({ criteriaKey: cursorCriteriaKey, pages: [{ after: '', offset: 0 }] })
  }, [paginationMode, cursorCriteriaKey])
  const resolvedQueryKey = paginationMode === 'pages'
    ? [...sourceQueryKey, activeCriteria, queryColumns, 'cursor-page', currentPage.after]
    : [...sourceQueryKey, activeCriteria, queryColumns]
  const displaySourceKey = paginationMode === 'pages' ? JSON.stringify(resolvedQueryKey) : sourceKey

  function restartInvalidCursor() {
    if (paginationMode !== 'pages') {
      queueMicrotask(() => void queryClient.resetQueries({ queryKey: resolvedQueryKey, exact: true }))
      return
    }
    queueMicrotask(() => setCursorPages((current) => current.criteriaKey === cursorCriteriaKey
      ? { criteriaKey: cursorCriteriaKey, pages: [{ after: '', offset: 0 }] } : current))
  }

  const refetchOnMount: 'always' = 'always'
  const query = useInfiniteQuery<
    DataGridResponse<Contract>,
    Error,
    InfiniteData<DataGridResponse<Contract>, string> | null,
    readonly unknown[],
    string
  >({
    queryKey: resolvedQueryKey,
    queryFn: async ({ pageParam, signal }) => {
      try {
        return await queryFn({
          signal,
          parameters,
          ...(paginationMode === 'pages' && options.queryFn ? { cacheKey: resolvedQueryKey } : {}),
          query: {
            ...activeCriteria,
            columns: queryColumns,
            pagination: { limit: pageSize, after: pageParam || undefined },
          } as unknown as DataGridQuery<Contract>,
        })
      } catch (error) {
        if (pageParam && invalidCursor(error)) {
          restartInvalidCursor()
        }
        throw error
      }
    },
    initialPageParam: paginationMode === 'pages' ? currentPage.after : '',
    getNextPageParam: (page) =>
      page.pageInfo.hasNextPage ? page.pageInfo.endCursor ?? undefined : undefined,
    ...(options.meta === undefined ? {} : { meta: options.meta }),
    ...(options.retry === undefined ? {} : { retry: options.retry }),
    gcTime: 0,
    ...(paginationMode === 'pages' ? { staleTime: 0, refetchOnMount } : {}),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: queryEnabled,
    placeholderData: (previous, previousQuery) => {
      if (paginationMode === 'pages') return undefined
      const previousSourceKey = JSON.stringify(previousQuery?.queryKey.slice(0, sourceQueryKey.length))
      return previousSourceKey === sourceKey ? previous : undefined
    },
  })
  const [successfulDisplay, setSuccessfulDisplay] = useState<{
    sourceKey: string
    data: InfiniteData<DataGridResponse<Contract>, unknown>
    columns: Column[]
    selected: Column[]
  } | null>(null)
  const failedDisplay = paginationMode === 'incremental' && query.data !== null
    && !query.data && (query.isError || query.isFetching)
    && successfulDisplay?.sourceKey === displaySourceKey
    ? successfulDisplay
    : null
  const isPreviousData = query.isPlaceholderData || failedDisplay !== null
  let displayData = query.data ?? failedDisplay?.data
  if (paginationMode === 'pages' && searchDebouncing) displayData = undefined
  const displayColumns = isPreviousData && successfulDisplay
    ? [
        ...columnOrder.filter((column) => successfulDisplay.columns.includes(column)),
        ...successfulDisplay.columns.filter((column) => !columnOrder.includes(column)),
      ]
    : orderedColumns
  const firstPage = displayData?.pages[0]
  const runtimeSchema = firstPage?.schema ?? {
    id: contract.id,
    keyColumn: contract.keyColumn,
    columns: Object.values(contract.columns),
    defaultSort: contract.defaultSort,
    searchMinimumLength: contract.searchMinimumLength,
  }
  const resolvedFacets: Partial<Record<Column, DataGridFacet>> = isPreviousData
    ? {}
    : firstPage?.facets ?? {}
  const runtimeColumnIds = new Set(runtimeSchema.columns.map((column) => column.id))
  const resolvedSelectedColumns = selectedColumns.filter((column) => runtimeColumnIds.has(column))
  for (const column of urlColumns ? [] : runtimeSchema.columns) {
    if (!(column.id in contract.columns) && !column.hiddenByDefault) {
      resolvedSelectedColumns.push(column.id as Column)
    }
  }
  const displayedSelectedColumns = isPreviousData
    ? successfulDisplay?.selected ?? resolvedSelectedColumns
    : resolvedSelectedColumns
  const resolvedOrderedColumns = [
    ...displayColumns,
    ...runtimeSchema.columns.map((column) => column.id as Column),
  ].filter((column, index, all) =>
    runtimeColumnIds.has(column)
    && displayedSelectedColumns.includes(column)
    && !hiddenColumns.has(column)
    && all.indexOf(column) === index)
  const facetCriteriaKey = (column: Column) => {
    const surroundingFilters = activeCriteria.filter?.filter((entry) => entry.column !== column)
    return JSON.stringify([
      sourceKey,
      {
        ...activeCriteria,
        filter: surroundingFilters?.length ? surroundingFilters : undefined,
      },
    ])
  }
  const activeFacetState = Object.fromEntries(
    Object.entries(facetState).filter(([column, state]) =>
      state?.criteriaKey === facetCriteriaKey(column as Column)),
  ) as typeof facetState
  const projectionKey = JSON.stringify([resolvedSelectedColumns, resolvedOrderedColumns])
  useEffect(() => {
    if (query.data === null || paginationMode === 'pages') {
      setSuccessfulDisplay(null)
      return
    }
    const completedData = query.data
    if (!query.isSuccess || query.isPlaceholderData || !completedData) return
    setSuccessfulDisplay((current) => {
      if (current?.sourceKey === displaySourceKey
        && current.data === completedData
        && JSON.stringify([current.selected, current.columns]) === projectionKey) return current
      return {
        sourceKey: displaySourceKey,
        data: completedData,
        columns: [...resolvedOrderedColumns],
        selected: [...resolvedSelectedColumns],
      }
    })
  }, [
    paginationMode,
    projectionKey,
    query.data,
    query.isPlaceholderData,
    query.isSuccess,
    resolvedOrderedColumns,
    resolvedSelectedColumns,
    displaySourceKey,
  ])

  function navigateSearch(patch: Record<string, unknown>, originId: string | null) {
    if (!urlState) {
      setMemory((previous) => ({ search: { ...previous.search, ...patch }, originId }))
      return Promise.resolve()
    }
    return navigate({
      to: '.',
      replace: true,
      search: (previous) => ({ ...previous, ...patch }),
      state: (previous) => withRecordedOrigin(previous, contract.id, originId),
    })
  }

  function writeSearch(patch: Record<string, unknown>, originId = originRef.current) {
    void navigateSearch(patch, originId)
  }

  function writeCriteria(next: DataGridCriteria<Column>) {
    writeSearch(encodeDataGridUrlState(next))
  }

  function writeColumns(update: (current: StoredColumns<Column>) => StoredColumns<Column>) {
    const next = update({ selected: selectedColumns, order: columnOrder })
    setStoredColumns(next)
    if (userId === undefined) return
    writeSearch({
      columns: encodeDataGridColumns(next.order.filter((column) => next.selected.includes(column))),
    })
  }

  function setParameter(parameter: Parameter, value: DataGridParameterValue) {
    writeSearch(encodeDataGridParameters(contract.parameters, { ...parameters, [parameter]: value }))
  }

  function savedViewSearch(filters: readonly DataGridFilter[], state: DataGridSavedViewState) {
    return normalizeDataGridUrlState({
      ...encodeDataGridUrlState({ filter: filters, sort: state.sort, search: state.search }),
      columns: encodeDataGridColumns(
        decodeDataGridColumns<Column>(state.columns?.join(','), contract.columns) ?? defaultColumns,
      ),
      ...encodeDataGridParameters(
        contract.parameters,
        decodeDataGridParameters(state.parameters ?? {}, contract.parameters),
      ),
    }, contract)
  }

  function tableFromSearch(search: Record<string, unknown>): DataGridTable {
    const decoded = decodeDataGridUrlState<Column>(search, contract.columns)
    return {
      filters: decoded.filter ?? [],
      columns: decodeDataGridColumns<Column>(search.columns, contract.columns)?.filter(shown) ?? storedOrderedColumns,
      sort: decoded.sort ?? contract.defaultSort,
      search: appliedDataGridSearch(decoded.search, contract.searchMinimumLength),
      parameters: decodeDataGridParameters(search, contract.parameters),
    }
  }

  function applySavedView(view: DataGridSavedView | null, { landing = false } = {}) {
    if (view === null) return navigateSearch(savedViewSearch([], {}), null)
    if (view.state === null) {
      const filter = view.filters.length ? view.filters : undefined
      return navigateSearch(landing
        ? {
            ...encodeDataGridUrlState({ filter }),
            columns: encodeDataGridColumns(storedOrder.filter((column) => storedSelectedColumns.includes(column))),
          }
        : encodeDataGridUrlState({ ...criteria, filter }), view.savedViewId)
    }
    return navigateSearch(savedViewSearch(view.filters, view.state), view.savedViewId)
  }

  function setSearch(value: string) {
    writeCriteria({
      ...criteria,
      search: value ? { ...criteria.search, query: value } : undefined,
    })
  }

  function setFilters(filter: readonly DataGridContractFilter<Contract>[]) {
    writeCriteria({ ...criteria, filter: filter.length ? filter : undefined })
  }

  function setSort(sort: readonly DataGridSort<Column>[]) {
    writeCriteria({ ...criteria, sort: sort.length ? sort : undefined })
  }

  function toggleSort(column: Column) {
    const current = activeCriteria.sort?.find((entry) => entry.column === column)
    setSort([{ column, direction: current?.direction === 'asc' ? 'desc' : 'asc' }])
  }

  async function loadFacetOptions(column: Column, search = '', append = false) {
    if (userId === undefined) return
    facetAbortRef.current[column]?.abort()
    const controller = new AbortController()
    facetAbortRef.current[column] = controller
    const currentCriteriaKey = facetCriteriaKey(column)
    const currentState = facetState[column]
    const current = currentState?.criteriaKey === currentCriteriaKey
      ? currentState.facet
      : resolvedFacets[column]
    setFacetState((state) => ({
      ...state,
      [column]: {
        ...(state[column]?.criteriaKey === currentCriteriaKey ? state[column] : {}),
        criteriaKey: currentCriteriaKey,
        search,
        isLoading: true,
        error: null,
      },
    }))
    try {
      const response = await queryFn({
        signal: controller.signal,
        parameters,
        query: {
          ...activeCriteria,
          columns: queryColumns,
          pagination: { limit: 1 },
          facets: [{
            column,
            search: search || undefined,
            pagination: {
              limit: 50,
              after: append ? current?.endCursor ?? undefined : undefined,
            },
          }],
        } as unknown as DataGridQuery<Contract>,
      })
      const incoming = response.facets?.[column]
      if (facetAbortRef.current[column] !== controller) return
      if (!search && !append && incoming && !incoming.hasMore
        && currentState && currentState.criteriaKey !== currentCriteriaKey) {
        const currentFilter = criteria.filter?.find((entry) =>
          entry.column === column && entry.operator === 'in')
        if (Array.isArray(currentFilter?.values)) {
          const availableValues = new Set(incoming.values?.map((option) => option.value))
          const nextValues = currentFilter.values.filter((value) => availableValues.has(value))
          if (nextValues.length !== currentFilter.values.length) {
            setFilters([
              ...(criteria.filter ?? []).filter((entry) => entry.column !== column),
              ...(nextValues.length ? [{ ...currentFilter, values: nextValues }] : []),
            ] as DataGridContractFilter<Contract>[])
          }
        }
      }
      setFacetState((state) => ({
        ...state,
        [column]: {
          criteriaKey: currentCriteriaKey,
          search,
          isLoading: false,
          error: null,
          facet: incoming && append && current
            ? { ...incoming, values: [...(current.values ?? []), ...(incoming.values ?? [])] }
            : incoming,
        },
      }))
    } catch (error) {
      if (!controller.signal.aborted && facetAbortRef.current[column] === controller) {
        setFacetState((state) => ({
          ...state,
          [column]: {
            ...state[column],
            criteriaKey: currentCriteriaKey,
            search,
            isLoading: false,
            error: error instanceof Error ? error : new Error('Couldn’t load filter options.'),
          },
        }))
      }
    }
  }

  async function exportAs(format: DataGridExportFormat) {
    if (!exportFn || exportingFormat !== null || userId === undefined) return
    const controller = new AbortController()
    exportAbortRef.current = controller
    setExportingFormat(format)
    setExportError(null)
    try {
      const exported = await exportFn({
        query: {
          ...activeCriteria,
          columns: orderedColumns,
        } as unknown as Omit<DataGridQuery<Contract>, 'pagination' | 'facets'>,
        signal: controller.signal,
        parameters,
        format,
      })
      if (!controller.signal.aborted) downloadBlob(exported.blob, exported.filename)
    } catch (error) {
      if (!controller.signal.aborted) {
        setExportError(error instanceof Error ? error : new Error(`Couldn’t export ${format === 'xlsx' ? 'Excel' : 'CSV'}.`))
      }
    } finally {
      if (exportAbortRef.current === controller) {
        exportAbortRef.current = null
        setExportingFormat(null)
      }
    }
  }

  const hasDisplayedData = displayData !== undefined
  const items = displayData?.pages.flatMap((page) => page.items) ?? []
  const pollInterval = typeof options.refetchInterval === 'function'
    ? (query.data ? options.refetchInterval(items) : false)
    : options.refetchInterval ?? false
  const [isPolling, setIsPolling] = useState(false)
  const [pollTick, setPollTick] = useState(0)
  const { refetch, isFetching } = query
  useEffect(() => {
    if (!pollInterval || !queryEnabled || isFetching || accessDenied(query.error)) return
    const timer = window.setTimeout(() => {
      if (document.visibilityState === 'hidden') {
        setPollTick((tick) => tick + 1)
        return
      }
      setIsPolling(true)
      void refetch().finally(() => setIsPolling(false))
    }, pollInterval)
    return () => window.clearTimeout(timer)
  }, [pollInterval, queryEnabled, isFetching, query.error, refetch, pollTick])
  // A scheduled poll refreshes rows in place; only a refresh the viewer started marks the grid as updating.
  const isUpdating = hasDisplayedData && query.isFetching && !query.isFetchingNextPage && !isPolling

  function previousPage() {
    if (query.isFetching || pageHistory.length < 2) return
    setCursorPages({ criteriaKey: cursorCriteriaKey, pages: pageHistory.slice(0, -1) })
  }

  function nextPage() {
    const endCursor = firstPage?.pageInfo.endCursor
    if (query.isFetching || !firstPage?.pageInfo.hasNextPage || !endCursor) return
    setCursorPages({ criteriaKey: cursorCriteriaKey,
      pages: [...pageHistory, { after: endCursor, offset: currentPage.offset + items.length }] })
  }

  return {
    contract,
    sourceKey: displaySourceKey,
    schema: hiddenColumns.size === 0 && baseColumns.size === 0 ? runtimeSchema : {
      ...runtimeSchema,
      columns: runtimeSchema.columns.filter((column) => !hiddenColumns.has(column.id)).map(lockFilter),
    },
    facets: resolvedFacets,
    facetState: activeFacetState,
    loadFacetOptions,
    criteria,
    activeCriteria,
    searchInput,
    searchDebouncing,
    selectedColumns: resolvedSelectedColumns,
    orderedColumns: resolvedOrderedColumns,
    query,
    items,
    hasDisplayedData,
    isPreviousData,
    isUpdating,
    canLoadMore: paginationMode === 'incremental' && !isPreviousData && !isUpdating && query.hasNextPage,
    pagination: paginationMode === 'pages' ? {
      start: items.length === 0 ? 0 : currentPage.offset + 1,
      end: currentPage.offset + items.length,
      totalCount: firstPage?.totalCount,
      hasPreviousPage: pageHistory.length > 1,
      hasNextPage: firstPage?.pageInfo.hasNextPage ?? false,
      previousPage,
      nextPage,
    } : null,
    setSearch,
    setFilters,
    setSort,
    toggleSort,
    parameters,
    setParameter,
    canExport: exportFn !== undefined && userId !== undefined,
    exportAs,
    exportingFormat,
    isExporting: exportingFormat !== null,
    exportError,
    savedViews: savedViewsState,
    awaitingDefaultView,
    cancelExport() {
      exportAbortRef.current?.abort()
      exportAbortRef.current = null
      setExportingFormat(null)
    },
    getColumn(column: Column) {
      const schema = runtimeSchema.columns.find((candidate) => candidate.id === column)
        ?? contract.columns[column]
      return {
        schema: schema && lockFilter(schema),
        visible: resolvedSelectedColumns.includes(column),
        sort: activeCriteria.sort?.find((entry) => entry.column === column),
        filter: criteria.filter?.find((entry) => entry.column === column),
        facet: activeFacetState[column]?.facet ?? resolvedFacets[column],
        toggleSort: () => toggleSort(column),
        setVisible: (visible: boolean) => writeColumns((current) => {
          const selected = visible
            ? [...new Set([...current.selected, column])]
            : current.selected.filter((candidate) => candidate !== column)
          return { ...current, selected: showsAnyColumn(selected) ? selected : defaultColumns }
        }),
      }
    },
    setSelectedColumns(selected: readonly Column[]) {
      writeColumns((current) => ({
        ...current,
        selected: showsAnyColumn(selected) ? [...selected] : defaultColumns,
      }))
    },
    setColumnOrder(order: readonly Column[]) {
      writeColumns((current) => ({
        ...current,
        order: fillVisibleSlots(current.order, order.filter((column) => current.order.includes(column))),
      }))
    },
    resetCriteria() {
      writeCriteria({})
    },
  }
}

function resolveRequests<Contract extends AnyDataGridContract>(
  options: UseDataGridOptions<Contract>,
): {
  queryFn: (input: DataGridRequest<Contract>) => Promise<DataGridResponse<Contract>>
  exportFn?: (input: DataGridExportRequest<Contract>) => Promise<DataGridExport>
} {
  if (options.endpoint !== undefined) {
    const client = createDataGridClient({
      contract: options.contract,
      endpoint: options.endpoint,
    })
    return {
      queryFn: options.queryFn ?? client.query,
      exportFn: options.exportFn ?? client.export,
    }
  }
  return { queryFn: options.queryFn, exportFn: options.exportFn }
}

function accessDenied(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('status' in error)) return false
  return error.status === 401 || error.status === 403
}

function invalidCursor(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('code' in error)) return false
  return typeof error.code === 'string' && error.code.toUpperCase().includes('CURSOR')
}

function parseStoredColumns<Column extends string>(
  raw: string,
  valid: ReadonlySet<string>,
): StoredColumns<Column> | undefined {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return undefined
    const selected = 'selected' in parsed && Array.isArray(parsed.selected)
      ? parsed.selected.filter((value): value is Column =>
        typeof value === 'string' && valid.has(value))
      : undefined
    const order = 'order' in parsed && Array.isArray(parsed.order)
      ? parsed.order.filter((value): value is Column =>
        typeof value === 'string' && valid.has(value))
      : undefined
    return selected && order ? { selected, order } : undefined
  } catch {
    return undefined
  }
}

function normalizeOrder<Contract extends AnyDataGridContract>(
  contract: Contract,
  stored: readonly ColumnId<Contract>[],
): ColumnId<Contract>[] {
  return [...stored, ...Object.keys(contract.columns)]
    .filter((column, index, all) => all.indexOf(column) === index)
    .filter((column): column is ColumnId<Contract> => column in contract.columns)
}

function fillVisibleSlots<Column extends string>(order: readonly Column[], visible: readonly Column[]): Column[] {
  let next = 0
  return order.map((column) => visible.includes(column) ? visible[next++]! : column)
}

function normalizeSelectedColumns<Contract extends AnyDataGridContract>(
  contract: Contract,
  stored: StoredColumns<ColumnId<Contract>>,
  defaults: readonly ColumnId<Contract>[],
): ColumnId<Contract>[] {
  const selected = stored.selected.filter((column) => column in contract.columns)
  if (!selected.length) return [...defaults]
  const unseen = defaults.filter((column) =>
    !stored.order.includes(column) && !selected.includes(column))
  return [...selected, ...unseen]
}

/** Maps dashboard field filters onto the columns that carry those fields; filters on other fields are dropped. */
export function gridFiltersFromFields(contract: AnyDataGridContract, filters: readonly FieldFilter[] | undefined) {
  if (!filters?.length) return []
  const columns = Object.values(contract.columns) as DataGridColumnSchema[]
  return filters.flatMap((filter) => {
    const column = columns.find((candidate) => candidate.field === filter.field && candidate.operators.includes(filter.operator))
    return column ? [{ column: column.id, operator: filter.operator, values: filter.values } as DataGridFilter] : []
  })
}
